import { useEffect, useMemo, useState } from 'react';

import { leadFilterOptions, leadTableDescriptor } from '@/components/features/Leads/tables/lead-table-descriptor';
import { LeadCard } from '@/components/features/Leads/tables/LeadCard';
import { LeadTable } from '@/components/features/Leads/tables/LeadTable';
import { CreateButton } from '@/components/layout/CreateButton';
import { PageContent } from '@/components/layout/PageContent';
import { PageHeader } from '@/components/layout/PageHeader';
import { buildToolbarDefinitions, quickFilterButtonLabel, serverSortKey, toFilterSpecMap } from '@/components/ui/embedded-resource-table/descriptor';
import { GenericToolbar, type ToolbarViewMode } from '@/components/ui/generic-toolbar/GenericToolbar';
import { PaginationControls } from '@/components/ui/pagination-controls';
import { Card, CardContent } from '@/components/ui/shadcn/card';
import { DatePicker } from '@/components/ui/shadcn/date-picker';
import { useSettings } from '@/contexts/SettingsContext';
import { useBreadcrumbs } from '@/hooks/use-breadcrumbs';
import { useColumnVisibility } from '@/hooks/use-column-visibility';
import { useDebounce } from '@/hooks/use-debounce';
import { useIndexFilters } from '@/hooks/use-index-filters';
import { useIsMobile } from '@/hooks/use-mobile';
import { formatDate } from '@/lib/utils';

import { Button } from '@/components/ui/shadcn/button';
import { useTranslations } from '@/hooks/useTranslations';
import AppLayout from '@/layouts/app-layout';
import {
    type Lead,
    type LeadMetrics,
    type PaginationMeta,
    type PriorityOption,
    type SourceConversion,
    type SourceOption,
    type StatusOption,
    type User,
    type UserPerformance,
} from '@/types';
import { Head, router, usePage } from '@inertiajs/react';
import { toast } from 'sonner';

interface UserOption {
    id: string;
    first_name: string;
    last_name: string;
    /** Every role they hold — what the assignee filter groups by. */
    roles?: string[];
    role?: string | null;
}

interface LeadsPageProps {
    leads: Lead[];
    pagination: PaginationMeta;
    filters: Record<string, string | string[] | number | undefined>;
    users: UserOption[];
    sources: SourceOption[];
    priorities: PriorityOption[];
    statuses: StatusOption[];
    currentUser: User;
    metrics?: LeadMetrics;
    statusCounts?: Record<string, number>;
    totalValueByStatus?: Record<string, number>;
    sourceCounts?: Record<string, number>;
    sourceConversions?: Record<string, SourceConversion>;
    userPerformance?: UserPerformance[];
    leadOfferApprovalThreshold?: number;
    availableYears?: string[];
    companies?: Array<{ id: string; name: string }>;
    availableFilterValues?: {
        company_id: string[];
    };
    [key: string]: unknown;
}

// Bumped when the object name became a default column: a stored set is taken as
// the user's full choice, so without a new key the column would stay hidden for
// everyone who has ever opened this page.
const COLUMN_STORAGE_KEY = 'leads:index:v2';

export default function LeadsIndex() {
    const { props } = usePage<LeadsPageProps>();
    const {
        leads,
        pagination,
        filters,
        statuses,
        priorities,
        users,
        companies,
        availableFilterValues,
        leadOfferApprovalThreshold,
        availableYears = [],
    } = props;
    const { t } = useTranslations();
    const { settings } = useSettings();
    const approvalThreshold = leadOfferApprovalThreshold ?? settings.leadOfferApprovalThreshold;

    // Columns, filters and sort entries all come from the descriptor, which the
    // detail-page tabs read too — that is what keeps the four renderings in step.
    const definitions = useMemo(() => buildToolbarDefinitions(leadTableDescriptor, t, { requireServerSort: true }), [t]);
    const filterSpecs = useMemo(() => toFilterSpecMap(leadTableDescriptor), []);

    const {
        columnFilters,
        handleFilterChange,
        toggleSingleFilter,
        clearFilters: clearServerFilters,
        applyFilters,
        loading,
    } = useIndexFilters(filterSpecs, filters, '/leads');

    const isMobile = useIsMobile();
    const [viewMode, setViewMode] = useState<ToolbarViewMode>(isMobile ? 'grid' : 'table');
    const [searchValue, setSearchValue] = useState(filters.search?.toString() ?? '');
    const debouncedSearchValue = useDebounce(searchValue, 800);
    const [columnFilterSearch, setColumnFilterSearch] = useState<Record<string, string>>({});
    const [sortColumn, setSortColumn] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);
    const {
        visibleColumns,
        showColumn: handleColumnShow,
        hideColumn: handleColumnHide,
    } = useColumnVisibility(COLUMN_STORAGE_KEY, definitions.defaultVisibleColumns, definitions.allColumnIds);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isSortOpen, setIsSortOpen] = useState(false);
    const [startDate, setStartDate] = useState<Date | undefined>(filters.date_from ? new Date(filters.date_from as string) : undefined);
    const [endDate, setEndDate] = useState<Date | undefined>(filters.date_to ? new Date(filters.date_to as string) : undefined);

    useEffect(() => {
        setViewMode(isMobile ? 'grid' : 'table');
    }, [isMobile]);

    useEffect(() => {
        const nextSearch = filters.search?.toString() ?? '';
        setSearchValue((previous) => (previous === nextSearch ? previous : nextSearch));
    }, [filters.search]);

    useEffect(() => {
        if ((filters.search?.toString() ?? '') === debouncedSearchValue) {
            return;
        }

        applyFilters({
            search: debouncedSearchValue || undefined,
            page: 1,
        });
    }, [applyFilters, debouncedSearchValue, filters.search]);

    // Every company that has leads, not just the ones on this page.
    const companyOptionSource = useMemo(() => {
        const availableCompanyIds = new Set(availableFilterValues?.company_id ?? []);

        return availableCompanyIds.size ? (companies ?? []).filter((company) => availableCompanyIds.has(company.id)) : (companies ?? []);
    }, [companies, availableFilterValues]);

    /**
     * Options come from whole-dataset reference lists rather than the rows on screen —
     * `availableYears` and `availableFilterValues` exist precisely so the filters can
     * offer values that are not on the current page.
     */
    const columnFilterOptions = useMemo(
        () =>
            leadFilterOptions({
                t,
                statuses,
                priorities,
                owners: users,
                companies: companyOptionSource,
                years: availableYears,
            }),
        [t, statuses, priorities, users, companyOptionSource, availableYears],
    );

    const handleSortChange = (column: string, direction: 'asc' | 'desc') => {
        setSortColumn(column);
        setSortDirection(direction);
        // `uuid` is the server's own fallback for an unrecognised key.
        applyFilters({ sort_by: serverSortKey(leadTableDescriptor, column) ?? 'uuid', sort_order: direction, page: 1 });
    };

    const clearSort = () => {
        setSortColumn(null);
        setSortDirection(null);
        applyFilters({ sort_by: undefined, sort_order: undefined, page: 1 });
    };

    const clearFilters = () => {
        setSearchValue('');
        setStartDate(undefined);
        setEndDate(undefined);
        // The spec map covers the checkbox filters; the date range is page-local.
        clearServerFilters({ date_from: undefined, date_to: undefined });
    };

    const handlePageChange = (page: number) => {
        applyFilters({ page });
    };

    const handlePerPageChange = (perPage: number) => {
        applyFilters({ per_page: perPage, page: 1 });
    };

    const handleStartDateChange = (date: Date | undefined) => {
        setStartDate(date);
        applyFilters({ date_from: date ? formatDate(date) : undefined, page: 1 });
    };

    const handleEndDateChange = (date: Date | undefined) => {
        setEndDate(date);
        applyFilters({ date_to: date ? formatDate(date) : undefined, page: 1 });
    };

    const parseDateInput = (value: string): Date | undefined => {
        if (!value) {
            return undefined;
        }

        const [year, month, day] = value.split('-').map(Number);
        if (!year || !month || !day) {
            return undefined;
        }

        return new Date(year, month - 1, day);
    };

    const formatDateInputValue = (date?: Date): string => {
        if (!date) {
            return '';
        }

        return formatDate(date);
    };

    const handleResetDates = () => {
        setStartDate(undefined);
        setEndDate(undefined);
        applyFilters({ date_from: undefined, date_to: undefined, page: 1 });
    };

    // Inline editing handlers
    const handleUpdate = {
        status: async (leadId: string, value: string) => {
            router.patch(
                `/leads/${leadId}/status`,
                {
                    status: value,
                },
                {
                    preserveState: true,
                    preserveScroll: true,
                    onSuccess: () => {
                        toast.success(t('leads.index.messages.status_updated', 'Status updated'));
                    },
                    onError: () => {
                        toast.error(t('leads.index.messages.failed_update_status', 'Failed to update status'));
                    },
                },
            );
        },
        priority: async (leadId: string, value: string) => {
            router.patch(
                `/leads/${leadId}/priority`,
                {
                    priority: value,
                },
                {
                    preserveState: true,
                    preserveScroll: true,
                    onSuccess: () => {
                        toast.success(t('leads.index.messages.priority_updated', 'Priority updated'));
                    },
                    onError: () => {
                        toast.error(t('leads.index.messages.failed_update_priority', 'Failed to update priority'));
                    },
                },
            );
        },
    };

    const breadcrumbs = useBreadcrumbs('leads');

    const paginationMeta = useMemo(() => {
        return {
            current_page: pagination.current_page ?? 1,
            last_page: pagination.last_page ?? 1,
            per_page: pagination.per_page ?? 25,
            total: pagination.total ?? leads.length,
            from: pagination.from ?? null,
            to: pagination.to ?? null,
        };
    }, [pagination, leads.length]);

    return (
        <AppLayout breadcrumbs={breadcrumbs}>
            <Head title={t('leads.index.title', 'Leads')} />
            <PageHeader
                title={t('leads.index.heading', 'Leads')}
                subtitle={t('leads.index.subheading', 'Manage your sales pipeline and track lead progress')}
                actions={
                    <CreateButton href="/leads/create" permission="leads.create" label={t('leads.index.actions.create_lead', '+ Create lead')} />
                }
            />

            <PageContent>
                {/* Toolbar */}
                <GenericToolbar
                    quickFilterButtonLabel={quickFilterButtonLabel(leadTableDescriptor, t, columnFilters)}
                    quickFilterDefinitions={definitions.quickFilterDefinitions}
                    columnDefinitions={definitions.columnDefinitions}
                    extraFilterDefinitions={definitions.extraFilterDefinitions}
                    columnFilterOptions={columnFilterOptions}
                    columnFilters={columnFilters}
                    columnFilterSearch={columnFilterSearch}
                    setColumnFilterSearch={setColumnFilterSearch}
                    toggleSingleFilter={toggleSingleFilter}
                    handleFilterChange={handleFilterChange}
                    clearFilters={clearFilters}
                    isFilterOpen={isFilterOpen}
                    setIsFilterOpen={setIsFilterOpen}
                    isSortOpen={isSortOpen}
                    setIsSortOpen={setIsSortOpen}
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    handleSortChange={handleSortChange}
                    clearSort={clearSort}
                    search={searchValue}
                    setSearch={setSearchValue}
                    perPage={paginationMeta.per_page}
                    onPerPageChange={handlePerPageChange}
                    viewMode={viewMode}
                    setViewMode={setViewMode}
                    visibleColumns={visibleColumns}
                    handleColumnShow={handleColumnShow}
                    handleColumnHide={handleColumnHide}
                    filteredCount={paginationMeta.total}
                    resourceName={leadTableDescriptor.resourceName}
                    resourceNamePlural={leadTableDescriptor.resourceNamePlural}
                    sortableColumns={definitions.sortableColumns}
                    customFiltersPosition="end"
                    customFilters={
                        <div className="space-y-3">
                            <div className="flex items-center justify-between text-sm font-medium">
                                <span>{t('leads.index.filters.date_range', 'Date range')}</span>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="text-muted-foreground h-auto px-2 py-1 text-xs"
                                    disabled={!startDate && !endDate}
                                    onClick={handleResetDates}
                                >
                                    {t('common.reset', 'Reset')}
                                </Button>
                            </div>
                            <div className="flex flex-col gap-2">
                                <div className="grid gap-1">
                                    <label className="text-muted-foreground text-xs">{t('leads.index.filters.from_date', 'From date')}</label>
                                    <DatePicker
                                        value={formatDateInputValue(startDate)}
                                        onValueChange={(value) => handleStartDateChange(parseDateInput(value))}
                                        placeholder={t('common.pick_date', 'Pick a date')}
                                    />
                                </div>

                                <div className="grid gap-1">
                                    <label className="text-muted-foreground text-xs">{t('leads.index.filters.to_date', 'To date')}</label>
                                    <DatePicker
                                        value={formatDateInputValue(endDate)}
                                        onValueChange={(value) => handleEndDateChange(parseDateInput(value))}
                                        placeholder={t('common.pick_date', 'Pick a date')}
                                    />
                                </div>
                            </div>
                        </div>
                    }
                />

                {/* Content */}
                {viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {leads.map((lead) => (
                            <LeadCard key={lead.id} lead={lead} />
                        ))}
                    </div>
                ) : (
                    <LeadTable
                        leads={leads}
                        onUpdate={handleUpdate}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSortChange={handleSortChange}
                        onColumnHide={handleColumnHide}
                        onColumnShow={handleColumnShow}
                        visibleColumns={visibleColumns}
                        columnFilters={columnFilters}
                        columnFilterOptions={columnFilterOptions}
                        onFilterChange={handleFilterChange}
                        statuses={statuses}
                        priorities={priorities}
                        approvalThreshold={approvalThreshold}
                    />
                )}

                <PaginationControls
                    pagination={paginationMeta}
                    onPageChange={handlePageChange}
                    onPerPageChange={handlePerPageChange}
                    loading={loading}
                    className="mt-6"
                />

                {viewMode === 'grid' && leads.length === 0 && (
                    <Card>
                        <CardContent className="py-12 text-center">
                            <p className="text-muted-foreground">{t('leads.index.empty.no_leads_found', 'No leads found matching your filters.')}</p>
                        </CardContent>
                    </Card>
                )}
            </PageContent>
        </AppLayout>
    );
}
